import React, { useCallback, useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import UploadManager from '../../components/admin/UploadManager';
import { calculateFinalPrice } from '../../lib/pricing';
import { SHIPPING_ORIGINS, normalizeOriginCode } from '../../lib/shippingOrigins';
import { DEFAULT_PREPARATION_DAYS, normalizePreparationDays } from '../../lib/preparationTime';
import { DEFAULT_GST_RATE, GST_CLASSIFICATIONS, getGstClassification } from '../../lib/gstClassifications';

const createEmptyVariant = () => ({ size: '', color: '', material: '', price_mrp: '', stock: 0, sku: '' });
const productPreparationDays = (value) => normalizePreparationDays(value);

export default function AdminProductsPage() {
  const { token, user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shippingOrigins, setShippingOrigins] = useState(SHIPPING_ORIGINS);
  const [siteDiscount, setSiteDiscount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    short_description: '',
    price_mrp: '',
    product_discount_pct: 0,
    category_id: '',
    stock: 10,
    sku: '',
    weight: '',
    dimensions: '',
    preparation_days: DEFAULT_PREPARATION_DAYS,
    origin_code: 'MY_PLACE',
    tax_class: 'unclassified',
    hsn_code: '',
    gst_rate: DEFAULT_GST_RATE,
    status: 'published',
    badges: '',
    images: [],
    variants: [],
  });

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, catRes, setRes] = await Promise.all([
        fetch('/api/products?includeDeleted=true', { headers: { 'x-auth-token': token } }),
        fetch('/api/categories'),
        fetch('/api/settings'),
      ]);

      const prodData = await prodRes.json();
      const catData = await catRes.json();
      const setData = await setRes.json();

      setProducts(Array.isArray(prodData) ? prodData : []);
      setCategories(Array.isArray(catData) ? catData : []);
      setSiteDiscount(Number(setData.site_discount_pct) || 0);
      if (setData.shipping_origins) {
        try {
          const parsedOrigins = typeof setData.shipping_origins === 'string'
            ? JSON.parse(setData.shipping_origins)
            : setData.shipping_origins;
          if (Array.isArray(parsedOrigins) && parsedOrigins.length > 0) setShippingOrigins(parsedOrigins);
        } catch (_error) {
          setShippingOrigins(SHIPPING_ORIGINS);
        }
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleOpenCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      short_description: '',
      price_mrp: '',
      product_discount_pct: 0,
      category_id: categories[0]?.id || '',
      stock: 10,
      sku: '',
      weight: '',
      dimensions: '',
      preparation_days: DEFAULT_PREPARATION_DAYS,
      origin_code: shippingOrigins[0]?.code || 'MY_PLACE',
      tax_class: 'unclassified',
      hsn_code: '',
      gst_rate: DEFAULT_GST_RATE,
      status: 'published',
      badges: 'featured',
      images: [],
      variants: [],
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      subtitle: product.subtitle || '',
      description: product.description || '',
      short_description: product.short_description || '',
      price_mrp: product.price_mrp || '',
      product_discount_pct: product.product_discount_pct || 0,
      category_id: product.category_id || '',
      stock: product.stock ?? 10,
      sku: product.sku || '',
      weight: product.weight || '',
      dimensions: product.dimensions || '',
      preparation_days: product.preparation_days ?? DEFAULT_PREPARATION_DAYS,
      origin_code: normalizeOriginCode(product.origin_code || shippingOrigins[0]?.code || 'MY_PLACE'),
      tax_class: product.tax_class || (product.hsn_code ? 'custom' : 'unclassified'),
      hsn_code: product.hsn_code || '',
      gst_rate: product.gst_rate ?? DEFAULT_GST_RATE,
      status: product.status || 'published',
      badges: product.badges || '',
      images: product.images || [],
      variants: Array.isArray(product.variants) ? product.variants.map((variant) => ({
        id: variant.id,
        size: variant.size || '',
        color: variant.color || '',
        material: variant.material || '',
        price_mrp: variant.price_mrp ?? '',
        stock: variant.stock ?? 0,
        sku: variant.sku || '',
      })) : [],
    });
    setShowModal(true);
  };

  const applyTaxClassification = (value) => {
    const classification = getGstClassification(value);
    const isCustom = classification.value === 'custom';
    setFormData((current) => ({
      ...current,
      tax_class: classification.value,
      hsn_code: isCustom ? current.hsn_code : classification.hsnCode,
      gst_rate: isCustom ? current.gst_rate : classification.gstRate,
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      variants: current.variants.map((variant, variantIndex) => (
        variantIndex === index ? { ...variant, [field]: value } : variant
      )),
    }));
  };

  const addVariant = () => {
    setFormData((current) => ({ ...current, variants: [...current.variants, createEmptyVariant()] }));
  };

  const removeVariant = (index) => {
    setFormData((current) => ({
      ...current,
      variants: current.variants.filter((_variant, variantIndex) => variantIndex !== index),
    }));
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const url = editingProduct ? `/api/products/${editingProduct.id}` : '/api/products';
      const method = editingProduct ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.msg || 'Save failed.');
      }

      setShowModal(false);
      triggerToast(editingProduct ? 'Product updated successfully! 🎉' : 'Product created successfully! 🎉');
      loadProducts();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteProduct = async (id, permanent = false) => {
    if (!window.confirm(`Are you sure you want to ${permanent ? 'permanently delete' : 'soft delete'} this product?`)) {
      return;
    }
    try {
      const res = await fetch(`/api/products/${id}?permanent=${permanent}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token },
      });
      if (res.ok) {
        triggerToast('Product deleted.');
        loadProducts();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRestoreProduct = async (id) => {
    try {
      const res = await fetch(`/api/products/${id}/restore`, {
        method: 'POST',
        headers: { 'x-auth-token': token },
      });
      if (res.ok) {
        triggerToast('Product restored!');
        loadProducts();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const triggerToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="brand-font m-0">Product Catalog Management</h2>
          <span className="text-muted small">Manage handmade items, 3-level discounts, inventory, and product images</span>
        </div>
        <button className="btn btn-primary-custom rounded-pill px-4" onClick={handleOpenCreateModal}>
          + Add New Product
        </button>
      </div>

      {toastMessage && (
        <div className="alert alert-success py-2 px-3 small text-center mb-3 fade show shadow-sm">
          {toastMessage}
        </div>
      )}

      {/* Products Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : (
        <div className="border rounded bg-card shadow-sm overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover align-middle m-0">
              <thead className="table-light">
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>MRP</th>
                  <th>Prod Disc %</th>
                  <th>Final Price</th>
                  <th>Prep time</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const catDiscount = categories.find((c) => c.id === p.category_id)?.category_discount_pct || 0;
                  const finalP = calculateFinalPrice(p.price_mrp, p.product_discount_pct, catDiscount, siteDiscount);

                  return (
                    <tr key={p.id} className={p.deleted_at ? 'table-secondary opacity-75' : ''}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={p.images?.[0] || 'https://via.placeholder.com/50'}
                            alt=""
                            className="rounded"
                            style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                          />
                          <div>
                            <span className="font-weight-bold d-block small">{p.title}</span>
                            <span className="text-muted small">SKU: {p.sku}</span>
                          </div>
                        </div>
                      </td>
                      <td className="small">{p.category_name || 'Uncategorized'}</td>
                      <td className="small">₹{p.price_mrp}</td>
                      <td>
                        <span className="badge bg-danger">{p.product_discount_pct}%</span>
                      </td>
                      <td className="font-weight-bold text-primary">₹{finalP}</td>
                      <td className="small">{productPreparationDays(p.preparation_days)} days</td>
                      <td>
                        <span className={`badge ${p.stock > 5 ? 'bg-success' : p.stock > 0 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                          {p.stock}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${p.status === 'published' ? 'bg-success' : 'bg-secondary'}`}>
                          {p.deleted_at ? 'DELETED' : p.status}
                        </span>
                      </td>
                      <td className="text-end">
                        {!p.deleted_at ? (
                          <>
                            <button
                              className="btn btn-xs btn-outline-primary me-1"
                              onClick={() => handleOpenEditModal(p)}
                            >
                              ✏️ Edit
                            </button>
                            {['admin', 'superadmin'].includes(user?.role) && (
                              <button
                                className="btn btn-xs btn-outline-danger"
                                onClick={() => handleDeleteProduct(p.id, false)}
                              >
                                🗑️ Delete
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            className="btn btn-xs btn-outline-success"
                            onClick={() => handleRestoreProduct(p.id)}
                          >
                            🔄 Restore
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal show d-block bg-dark bg-opacity-50" tabIndex="-1">
          <div className="modal-dialog modal-dialog-custom">
            <div className="modal-content modal-content-custom bg-card">
              <form onSubmit={handleSaveProduct} className="d-flex flex-column h-100 m-0">
                <div className="modal-header">
                  <h5 className="modal-title brand-font">
                    {editingProduct ? 'Edit Product' : 'Create New Handmade Product'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>

                <div className="modal-body modal-body-custom">
                  <div className="row g-3">
                    <div className="col-12 col-md-8">
                      <label className="form-label font-weight-bold small">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label font-weight-bold small">Category</label>
                      <select
                        className="form-select"
                        value={formData.category_id}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      >
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label font-weight-bold small">Original MRP (₹) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        required
                        value={formData.price_mrp}
                        onChange={(e) => setFormData({ ...formData, price_mrp: e.target.value })}
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label font-weight-bold small">Product Discount %</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="100"
                        value={formData.product_discount_pct}
                        onChange={(e) => setFormData({ ...formData, product_discount_pct: e.target.value })}
                      />
                    </div>

                    <div className="col-12 border rounded p-3 bg-background">
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label font-weight-bold small" htmlFor="product-tax-class">Invoice tax classification</label>
                          <select
                            id="product-tax-class"
                            className="form-select"
                            value={formData.tax_class}
                            onChange={(e) => applyTaxClassification(e.target.value)}
                          >
                            {GST_CLASSIFICATIONS.map((classification) => (
                              <option key={classification.value} value={classification.value}>{classification.label}</option>
                            ))}
                          </select>
                          <div className="form-text">Stored on each order line. Use Custom when the material, HSN, or value band needs a tax adviser’s confirmation.</div>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label font-weight-bold small" htmlFor="product-hsn-code">HSN code</label>
                          <input
                            id="product-hsn-code"
                            type="text"
                            maxLength="20"
                            className="form-control"
                            value={formData.hsn_code}
                            onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label font-weight-bold small" htmlFor="product-gst-rate">GST rate (%)</label>
                          <input
                            id="product-gst-rate"
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className="form-control"
                            value={formData.gst_rate}
                            onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label font-weight-bold small">Stock Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label font-weight-bold small">SKU</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Auto-generated if empty"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label font-weight-bold small">Short Subtitle / Highlight</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.subtitle}
                        onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label font-weight-bold small">Short Product Description</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.short_description}
                        onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      ></textarea>
                    </div>

                    <div className="col-12">
                      <label className="form-label font-weight-bold small">Full Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      ></textarea>
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label font-weight-bold small">Weight (g)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label font-weight-bold small" htmlFor="product-preparation-days">Preparation time (business days)</label>
                      <input
                        id="product-preparation-days"
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        className="form-control"
                        value={formData.preparation_days}
                        onChange={(e) => setFormData({ ...formData, preparation_days: e.target.value })}
                      />
                      <div className="form-text">Time needed to make and pack the product before dispatch. Defaults to 7 days.</div>
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label font-weight-bold small">Dimensions</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 10 × 8 × 4 cm"
                        value={formData.dimensions}
                        onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label font-weight-bold small" htmlFor="product-origin">Ships from</label>
                      <select
                        id="product-origin"
                        className="form-select"
                        value={formData.origin_code}
                        onChange={(e) => setFormData({ ...formData, origin_code: e.target.value })}
                      >
                        {shippingOrigins.filter((origin) => origin.active !== false).map((origin) => (
                          <option key={origin.code} value={origin.code}>{origin.label} · {origin.city}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label font-weight-bold small">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label font-weight-bold small">Badges</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. new, bestseller, featured"
                        value={formData.badges}
                        onChange={(e) => setFormData({ ...formData, badges: e.target.value })}
                      />
                      <div className="form-text">Use comma-separated badges. They appear on product cards.</div>
                    </div>

                    <div className="col-12 border-top pt-3 mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <label className="form-label font-weight-bold small mb-0">Size options and pricing</label>
                          <div className="form-text">Add one row per size. Each row controls its own MRP and stock; product stock is summed automatically.</div>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={addVariant}>+ Add size</button>
                      </div>
                      {formData.variants.length === 0 && (
                        <div className="small text-muted border rounded p-3">No size options. The product uses its default MRP and stock above.</div>
                      )}
                      {formData.variants.map((variant, index) => (
                        <div className="row g-2 align-items-end border rounded p-2 mb-2" key={variant.id || `new-${index}`}>
                          <div className="col-12 col-md-2">
                            <label className="form-label small">Size *</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Small, Medium"
                              required
                              value={variant.size}
                              onChange={(e) => updateVariant(index, 'size', e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-2">
                            <label className="form-label small">Color / style</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={variant.color}
                              onChange={(e) => updateVariant(index, 'color', e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-2">
                            <label className="form-label small">Material</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={variant.material}
                              onChange={(e) => updateVariant(index, 'material', e.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-2">
                            <label className="form-label small">MRP (₹) *</label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              className="form-control form-control-sm"
                              required
                              value={variant.price_mrp}
                              onChange={(e) => updateVariant(index, 'price_mrp', e.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-2">
                            <label className="form-label small">Stock *</label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="form-control form-control-sm"
                              required
                              value={variant.stock}
                              onChange={(e) => updateVariant(index, 'stock', e.target.value)}
                            />
                          </div>
                          <div className="col-10 col-md-1">
                            <label className="form-label small">SKU</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={variant.sku}
                              onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                            />
                          </div>
                          <div className="col-2 col-md-1">
                            <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeVariant(index)} aria-label={`Remove size option ${index + 1}`}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Host API Upload Manager */}
                    <div className="col-12 border-top pt-3 mt-2">
                      <UploadManager
                        images={formData.images}
                        onImagesChange={(imgs) => setFormData({ ...formData, images: imgs })}
                        token={token}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary-custom px-4">
                    Save Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
